import { Helmet } from "react-helmet-async";

export const SITE_URL = (process.env.REACT_APP_SITE_URL || "https://www.collezionatico.com").replace(/\/$/, "");
export const SITE_NAME = "Collezionatico";
const GSC = process.env.REACT_APP_GSC_VERIFICATION || "";

export function clip(text, max = 180) {
  const t = (text || "").replace(/\s+/g, " ").trim();
  if (t.length <= max) return t;
  const cut = t.slice(0, max);
  return `${cut.slice(0, cut.lastIndexOf(" ")).replace(/[,;:.]$/, "")}…`;
}

export function Seo({
  title, fullTitle, description, path = "", image, imageAlt,
  type = "website", noindex = false, published, modified, jsonLd,
}) {
  const url = `${SITE_URL}${path}`;
  const pageTitle = fullTitle || (title ? `${title} — ${SITE_NAME}` : SITE_NAME);
  const abs = image
    ? (image.startsWith("/") ? `${SITE_URL}${image}` : image)
    : `${SITE_URL}/brand/logo-share.jpg`;
  const blocks = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet prioritizeSeoTags>
      <title>{pageTitle}</title>
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={url} />
      <link rel="alternate" type="application/rss+xml" title={`${SITE_NAME} — Feed RSS`} href={`${SITE_URL}/api/feed.xml`} />
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large"} />
      {GSC && <meta name="google-site-verification" content={GSC} />}

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="it_IT" />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={pageTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={url} />
      {abs && <meta property="og:image" content={abs} />}
      {abs && imageAlt && <meta property="og:image:alt" content={imageAlt} />}
      {published && <meta property="article:published_time" content={published} />}
      {modified && <meta property="article:modified_time" content={modified} />}

      <meta name="twitter:card" content={abs ? "summary_large_image" : "summary"} />      <meta name="twitter:title" content={pageTitle} />
      {description && <meta name="twitter:description" content={description} />}
      {abs && <meta name="twitter:image" content={abs} />}

      {blocks.map((block, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(block)}</script>
      ))}
    </Helmet>
  );
}

export const organizationLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE_NAME,
  url: SITE_URL,
  logo: `${SITE_URL}/icon-512.png`,
  description: "Portale italiano di notizie, guide e valori indicativi sul collezionismo.",
  email: "elefg24@gmail.com",
};

export const websiteLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE_NAME,
  alternateName: "Collezionatico — il portale dei collezionisti",
  url: SITE_URL,
  inLanguage: "it-IT",
  publisher: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
  potentialAction: {
    "@type": "SearchAction",
    target: { "@type": "EntryPoint", urlTemplate: `${SITE_URL}/cerca?q={search_term_string}` },
    "query-input": "required name=search_term_string",
  },
};

export function breadcrumbLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem", position: i + 1, name: it.name, item: `${SITE_URL}${it.path}`,
    })),
  };
}

export function articleLd(article, path) {
  const img = article.image_url?.startsWith("/") ? `${SITE_URL}${article.image_url}` : article.image_url;
  return {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    headline: article.title?.slice(0, 110),
    description: article.summary,
    image: img ? [img] : undefined,
    datePublished: article.published_at || article.created_at,
    dateModified: article.updated_at || article.published_at || article.created_at,
    inLanguage: "it-IT",
    mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}${path}` },
    author: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
    publisher: {
      "@type": "Organization", name: SITE_NAME,
      logo: { "@type": "ImageObject", url: `${SITE_URL}/icon-512.png`, width: 512, height: 512 },
    },
    articleSection: article.category,
    keywords: (article.tags || []).join(", ") || undefined,
    isBasedOn: article.source_url || undefined,
  };
}
